import type { JSX } from 'react';
import React from 'react';
import { DrawType, FeatureType, LoanStatusType } from '@apply/constants';
import { DrawApi } from '@apply/api';
import { formatCurrencyOrUndefined, RequestDrawModal, RequestDrawModalV2 } from '../../../../../../ui/src';
import { Dispatch, SetStateAction, useState } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { useAuthentication } from '../../../../../../../apps/bfi-apply/src/app/Login/hooks/useAuthentication';

interface IAddDrawButtonParameters {
    drawBudget: DrawApi.IDrawBudget | undefined;
    set_drawBudget: Dispatch<SetStateAction<DrawApi.IDrawBudget | undefined>>;
    applicationInsights: ApplicationInsights;
    isAdmin: boolean;
    isApproved: boolean;
    isFunded: boolean;
    isEquityMet: boolean;
    isBudgetMaxed: boolean;
    bankAccountLast4Digits?: string;
    borrowerName: string;
    isReadOnly?: boolean;
}
export const AddDrawButton = ({
    drawBudget,
    set_drawBudget,
    applicationInsights,
    isAdmin,
    isApproved,
    isFunded,
    isEquityMet,
    isBudgetMaxed,
    bankAccountLast4Digits,
    borrowerName,
    isReadOnly = false,
}: IAddDrawButtonParameters): JSX.Element => {
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    //Used for Apply side only validation of a feature - Draws is a shared control
    const { isFeatureEnabled } = useAuthentication();

    const checkApplyV2Enabled = () => {
        if (isAdmin) {
            return false;
        } else {
            return isFeatureEnabled(FeatureType.ApplyV2);
        }
    };

    const [isDrawModalOpen, set_isDrawModalOpen] = useState<boolean>(false);

    const ReadOnlyAddButton = ({ title }) => {
        return (
            <button
                id='addDrawButton'
                className='button primary readOnly'
                title={title}
                disabled={true}
            >
                Add Draw
            </button>
        );
    };

    const onAddDrawClick = async () => {
        set_isDrawModalOpen(true);
    };

    const handleCloseEvent = () => {
        set_isDrawModalOpen(false);
    };

    const submitDrawRequest = async ({
        drawBudgetId,
        drawType,
        fontId,
        ipAddress,
    }: {
        drawBudgetId: number;
        drawType: DrawType;
        fontId: number | undefined;
        ipAddress: string | undefined;
    }) => {
        set_isDrawModalOpen(false);

        const data = await drawApi.addDraw({
            drawBudgetId,
            drawType,
            fontId,
            ipAddress,
        });

        const budget = data.result;
        set_drawBudget(budget);
    };

    if (isAdmin) {
        const isApproved = !!drawBudget?.currentApproval;

        const isLastDrawFinalized =
            drawBudget?.drawCount === 0 || (drawBudget?.draws && !!drawBudget?.draws[drawBudget.drawCount - 1]?.isFinalized);

        const isAllowableStatus = drawBudget?.loanStatusId === LoanStatusType.Outstanding;

        const budgetTotal = drawBudget?.drawBudgetItems.reduce((total, item) => {
            return (item?.budgetAmount ?? 0) - (item?.equityAmount ?? 0) + total;
        }, 0);

        const drawTotal = drawBudget?.drawBudgetItems.reduce((previousValue, budgetItem) => {
            return previousValue + (budgetItem.drawItems?.reduce((sum, draw) => sum + (draw?.funded ?? 0), 0) ?? 0);
        }, 0);

        const isFullyFunded = (drawTotal ?? 0) >= (budgetTotal ?? 0);

        if (isReadOnly) return <ReadOnlyAddButton title={'This budget is read only.'} />;

        if (!isApproved) return <ReadOnlyAddButton title={'Budget must be approved before adding draws.'} />;

        if (!isAllowableStatus)
            return <ReadOnlyAddButton title={'Draws can only be added if the loan is in Outstanding status.'} />;

        if (!isEquityMet)
            return (
                <ReadOnlyAddButton
                    title={`The equity requirement of ${formatCurrencyOrUndefined(drawBudget?.equityRequirement, 0)} must be met before the first draw.`}
                />
            );

        if (!isLastDrawFinalized)
            return <ReadOnlyAddButton title={`Please fund Draw #${drawBudget?.drawCount} to add another draw.`} />;

        if (isFullyFunded) return <ReadOnlyAddButton title={'This budget has been fully funded.'} />;
    } else {
        if (isReadOnly) return <></>;
        if (!isApproved) return <></>;
        if (!isFunded) return <></>;
        if (!isEquityMet) return <></>;
        if (isBudgetMaxed) return <></>;
    }

    return (
        <>
            <button
                id='addDrawButton'
                className='button primary'
                onClick={onAddDrawClick}
            >
                Add Draw
            </button>
            {checkApplyV2Enabled() ? (
                <RequestDrawModalV2
                    drawBudgetId={drawBudget?.id}
                    currentAccountNumber={bankAccountLast4Digits}
                    borrowerName={borrowerName}
                    open={isDrawModalOpen}
                    submitDrawRequest={submitDrawRequest}
                    handleCloseEvent={handleCloseEvent}
                    isAdmin={isAdmin}
                    applicationInsights={applicationInsights}
                    isOutOfBalance={drawBudget?.isOutOfBalance}
                    isOutOfBalanceWaived={drawBudget?.isOutOfBalanceWaived}
                />
            ) : (
                <RequestDrawModal
                    drawBudgetId={drawBudget?.id}
                    currentAccountNumber={bankAccountLast4Digits}
                    borrowerName={borrowerName}
                    open={isDrawModalOpen}
                    submitDrawRequest={submitDrawRequest}
                    handleCloseEvent={handleCloseEvent}
                    isAdmin={isAdmin}
                    applicationInsights={applicationInsights}
                    isOutOfBalance={drawBudget?.isOutOfBalance}
                    isOutOfBalanceWaived={drawBudget?.isOutOfBalanceWaived}
                />
            )}
        </>
    );
};
